'use client';

/** @jsxImportSource react */

import Link from 'next/link';

import { ONARK_TRAINING_PATH } from '@marketing/lib/training';
import { catalog } from '@onark/observability';
import type { ReactNode } from 'react';

import { ctaClass } from './SignupTrialForm';

const TARGETS = {
  training: {
    event: 'signup_trial.training_cta_clicked',
    href: ONARK_TRAINING_PATH,
  },
  trial: { event: 'signup_trial.trial_cta_clicked', href: '/sign-up' },
} as const;

/**
 * The lime pill every /start form submits with. Composed HERE, not in the
 * server page: `ctaClass` is exported from a 'use client' module, and a server
 * component importing a non-component value from one gets a client reference,
 * not the string.
 */
const buttonClass = `${ctaClass} inline-flex items-center justify-center px-7 py-3.5 text-[16px]`;

/**
 * The link into the free OnArk training class, for the /start landings.
 *
 * Not `@marketing/components/training-link`: that one reports through the
 * marketing app's `track`, which only initializes under app/(marketing)/layout.
 * Rendered on /start it links fine and reports nothing, so the one number the
 * training door exists to produce (landers → training) would be missing.
 * This emits through the catalog, keyed on `landing` like the rest of the
 * signup_trial funnel.
 *
 * `to="trial"` is the same link pointed at /sign-up, for the secondary
 * free-trial ask on a training-first landing. Same component so the two asks
 * emit the same shape and can be compared by door and placement.
 */
export default function TrainingCta({
  children,
  className,
  landing,
  source,
  to = 'training',
  variant = 'link',
}: {
  children: ReactNode;
  className?: string;
  /** Funnel door label — the /start slug. */
  landing: string;
  /** Placement on the page: hero, closing, … */
  source: string;
  /** Where the link goes: the free training class (default) or the trial. */
  to?: keyof typeof TARGETS;
  /** 'button' renders the shared lime CTA pill; 'link' leaves styling to className. */
  variant?: 'button' | 'link';
}) {
  return (
    <Link
      className={
        variant === 'button' ? `${buttonClass} ${className ?? ''}` : className
      }
      href={TARGETS[to].href}
      onClick={() => catalog.emit(TARGETS[to].event, { landing, source })}
    >
      {children}
    </Link>
  );
}
